import { Plus, X } from "lucide-react";
import { GRID_TYPES, HANDLES, LOCKS, OPERABLE, PATTERNS, newGrid } from "@/lib/design";

const inp = "h-11 w-full rounded-[10px] border border-black/10 bg-white px-3 text-[15px] text-black outline-none focus:border-[#007AFF] disabled:opacity-40";
const Lab = ({ t, children }) => <label className="block text-[12px] font-semibold uppercase tracking-wide text-[#6D6D72]">{t}<div className="mt-1 normal-case tracking-normal">{children}</div></label>;
const Seg = ({ value, options, onChange, testId }) => (
  <div className="grid grid-cols-2 gap-1 rounded-[10px] bg-black/[0.05] p-1" data-testid={testId}>
    {options.map(([k, l]) => (
      <button key={k} data-testid={`${testId}-${k}`} onClick={() => onChange(k)}
        className={`h-9 rounded-[8px] text-[14px] font-medium transition-colors ${value === k ? "bg-white text-black shadow-sm" : "text-[#3C3C43]"}`}>{l}</button>
    ))}
  </div>
);

// Section fill (glass / solid panel), grids (type + pattern) and, for operable sashes/leaves, handle and lock.
export function GridPanel({ n, r, p }) {
  const g = n.grid;
  const solid = n.fill === "solid";
  const pg = (x) => p({ grid: { ...g, ...x } });
  const num = (v, lo, hi) => Math.max(lo, Math.min(hi, Number(v) || lo));
  return (
    <div className="space-y-3" data-testid="design-grid-panel">
      <Lab t="Fill"><Seg testId="section-fill" value={n.fill || "glass"} options={[["glass", "Glass"], ["solid", "Solid panel"]]} onChange={(v) => p({ fill: v })} /></Lab>
      <label className="flex items-center gap-2 text-[15px] text-black">
        <input type="checkbox" data-testid="section-grid-toggle" disabled={solid} checked={!!g && !solid} onChange={(e) => p({ grid: e.target.checked ? newGrid() : null })} className="h-5 w-5" />
        Grids on this section{solid && <span className="text-[13px] text-[#6D6D72]">(not on solid panels)</span>}
      </label>
      {g && !solid && (
        <div className="space-y-3 rounded-[12px] bg-black/[0.03] p-3">
          <Lab t="Grid type"><select data-testid="grid-type-select" value={g.type || "btg"} onChange={(e) => pg({ type: e.target.value })} className={inp}>
            {GRID_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></Lab>
          <Lab t="Pattern"><select data-testid="grid-pattern-select" value={g.pattern || "colonial"} onChange={(e) => pg({ pattern: e.target.value, ...(e.target.value === "sunburst" ? { cols: Math.max(5, g.cols || 0) } : e.target.value === "diamond" ? { cols: Math.max(3, g.cols || 0) } : {}) })} className={inp}>
            {PATTERNS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></Lab>
          {g.pattern === "colonial" && <div className="grid grid-cols-2 gap-2">
            <Lab t="Across"><input data-testid="section-grid-cols" inputMode="numeric" value={g.cols} onChange={(e) => pg({ cols: num(e.target.value, 1, 12) })} className={inp} /></Lab>
            <Lab t="Down"><input data-testid="section-grid-rows" inputMode="numeric" value={g.rows} onChange={(e) => pg({ rows: num(e.target.value, 1, 12) })} className={inp} /></Lab>
          </div>}
          {g.pattern === "prairie" && <Lab t="Border width (in)"><input data-testid="grid-border-input" inputMode="decimal" value={g.border ?? 3} onChange={(e) => pg({ border: num(e.target.value, 1, 12) })} className={inp} /></Lab>}
          {["diamond", "sunburst"].includes(g.pattern) && <Lab t={g.pattern === "diamond" ? "Diamonds across" : "Rays"}>
            <input data-testid="grid-count-input" inputMode="numeric" value={g.cols} onChange={(e) => pg({ cols: num(e.target.value, 2, 12) })} className={inp} /></Lab>}
          {g.pattern === "custom" && (
            <div className="space-y-2" data-testid="grid-custom-bars">
              <p className="text-[13px] text-[#6D6D72]">Drag a bar on the drawing, or type where it sits.</p>
              {(g.bars || []).map((b, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="w-24 text-[14px]">{b.dir === "v" ? "Vertical" : "Horizontal"}</span>
                  <input data-testid={`grid-bar-at-${i}`} inputMode="decimal" value={b.at} onChange={(e) => pg({ bars: g.bars.map((x, j) => (j === i ? { ...x, at: Number(e.target.value) || 0 } : x)) })} className={inp} />
                  <button aria-label="Remove bar" data-testid={`grid-bar-remove-${i}`} onClick={() => pg({ bars: g.bars.filter((_, j) => j !== i) })} className="p-2 text-[#8E8E93]"><X className="h-4 w-4" /></button>
                </div>
              ))}
              <div className="grid grid-cols-2 gap-2">
                {[["v", "Vertical bar"], ["h", "Horizontal bar"]].map(([dir, l]) => (
                  <button key={dir} data-testid={`grid-add-bar-${dir}`} onClick={() => pg({ bars: [...(g.bars || []), { dir, at: Math.round(((dir === "v" ? r.w : r.h) / 2 - 2) * 8) / 8 }] })}
                    className="inline-flex h-10 items-center justify-center gap-1 rounded-full border border-black/10 bg-white text-[14px]"><Plus className="h-4 w-4" />{l}</button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {OPERABLE.includes(n.type) && (
        <div className="grid grid-cols-2 gap-2">
          <Lab t="Handle"><select data-testid="section-handle-select" value={n.handle || "standard"} onChange={(e) => p({ handle: e.target.value })} className={inp}>
            {HANDLES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></Lab>
          <Lab t="Lock"><select data-testid="section-lock-select" value={n.lock || "standard"} onChange={(e) => p({ lock: e.target.value })} className={inp}>
            {LOCKS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></Lab>
        </div>
      )}
    </div>
  );
}
